import { useEffect, useMemo, useState } from "react";

import { EPermissionVerbs, EUnitPermissionAreas } from "@m7-health/shared-utils";

import { useAppDispatch, useAppSelector, useAppStore } from "~/common/hooks/useRedux";
import { useAppConfigQuery, useCurrentUserQuery } from "~/features/User/queries";
import { setUser } from "~/features/User/store";

import { facilityLevelPermissionAreas } from "#/features/Roster/SchedulerRoster/AddEditPage/SchedulerDataForm/types";
import { IPermissions, useListStaffDetailsQuery } from "@/api";

import { httpClient } from "../packages/httpClient";

import { useCurrentRole } from "./useCurrentRole";
import { useKeyBy } from "./utils";

export const useUser = () => {
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const { data } = useCurrentUserQuery();

  useEffect(() => {
    if (!data) return;

    if (JSON.stringify(store.getState().user.userData) !== JSON.stringify(data)) {
      dispatch(setUser(data));
    }
  }, [data, dispatch, store]);
};

export const useCurrentActAs = () => {
  const [actAs, setActAs] = useState<string | null>(null);

  useEffect(() => {
    const handleActAsChange = () => setActAs(httpClient.getActAs());
    httpClient.eventEmitter.on("actAsChanged", handleActAsChange);

    handleActAsChange();

    return () => {
      httpClient.eventEmitter.removeListener("actAsChanged", handleActAsChange);
    };
  }, []);

  return actAs;
};

export const useCurrentUser = () => useAppSelector((state) => state.user.userData);
export const useCurrentUserId = () => useAppSelector((state) => state.user.userData?.id);
/**
 * ONLY to be used from the Staff App.
 * A custom hook to fetch the current staff member's details.
 *
 * @returns The details of the current staff member, or undefined if not available.
 *
 * @remarks
 * This hook combines several other hooks and queries to determine if the current user
 * is a staff member and fetch their details if they are.
 *
 * It uses:
 * - useCurrentUser: To get the current user's data
 * - useCurrentRole: To check if the current user is staff
 * - useListStaffDetailsQuery: To fetch the staff details
 *
 * The staff details query is skipped if the user is not logged in or if the app
 * is not the staff app.
 */
export const useCurrentStaffDetails = () => {
  const currentUser = useCurrentUser();
  const { userIsStaff } = useCurrentRole();

  const { data } = useListStaffDetailsQuery(
    {
      staffIds: [currentUser?.id],
    },
    {
      skip: !currentUser?.id || !userIsStaff,
    },
  );

  return data?.[0];
};
export const useCurrentStaffHomeUnit = () => {
  const currentUserDetails = useCurrentStaffDetails();
  const units = useAppConfigQuery().data?.units;

  return useMemo(() => {
    if (!currentUserDetails) return null;

    return units?.find((unit) => unit.id === currentUserDetails.homeUnitId);
  }, [currentUserDetails, units]);
};
export const useCurrentStaffHomeUnitId = () => useCurrentStaffHomeUnit()?.id;

// to only be used inside useCheckUserPermission OR with schedulerRoster / houseView permissions
export const useCurrentUserPermissions = (): {
  permissionsDataByUnit: Record<string, Partial<Record<EUnitPermissionAreas, EPermissionVerbs>>>;
  permissionsDataByFacility: Record<
    string,
    Partial<Record<EUnitPermissionAreas, EPermissionVerbs>>
  >;
} => {
  const appConfig = useAppConfigQuery().data;
  const permissions = appConfig?.schedulerPermissions;
  const unitsById = useKeyBy(appConfig?.units, "id");
  return useMemo(() => {
    const permissionsDataByUnit: Record<
      string,
      Partial<Record<IPermissions["area"], IPermissions["permission"]>>
    > = {};
    const permissionsDataByFacility: Record<
      string,
      Partial<Record<IPermissions["area"], IPermissions["permission"]>>
    > = {};

    permissions?.forEach((permissionData) => {
      const permissionUnitId = permissionData.roster.unitId;
      const permissionFacilityId = permissionUnitId && unitsById[permissionUnitId]?.facilityId;
      if (facilityLevelPermissionAreas.includes(permissionData.area) && permissionFacilityId)
        permissionsDataByFacility[permissionFacilityId] = {
          ...(permissionsDataByFacility[permissionFacilityId] || {}),
          [permissionData.area]: permissionData.permission,
        };
      else if (!facilityLevelPermissionAreas.includes(permissionData.area) && permissionUnitId) {
        permissionsDataByUnit[permissionUnitId] = {
          ...(permissionsDataByUnit[permissionUnitId] || {}),
          [permissionData.area]: permissionData.permission,
        };
      }
    });
    return {
      permissionsDataByUnit,
      permissionsDataByFacility,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(permissions)]);
};
